import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import axios from "axios";
import { AiAvatar, KIOSK_MESSAGES } from "../../components/ui/ai-avatar";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IDLE_TIMEOUT = 120;

export default function KioskLookup() {
  const navigate = useNavigate();
  const [vehicleReg, setVehicleReg] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const idleTimerRef = useRef(null);

  // Idle detection
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown', 'input'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  const handleLookup = async () => {
    if (!vehicleReg.trim()) {
      setError("Please enter your vehicle registration");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/lookup/${encodeURIComponent(vehicleReg.trim())}`);
      
      navigate("/kiosk/status", { 
        state: { 
          lookupResult: response.data,
          vehicleReg: vehicleReg.trim().toUpperCase()
        } 
      });
    } catch (err) {
      console.error("Lookup error:", err);
      setError("Unable to check registration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLookup();
    }
  };

  return (
    <div className="kiosk-container flex flex-col min-h-screen p-6 md:p-12" data-testid="kiosk-lookup">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <ParkWiseLogo size="lg" />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center max-w-2xl mx-auto w-full">
        {/* AI Avatar */}
        <div className="mb-6">
          <AiAvatar 
            message={KIOSK_MESSAGES.lookup} 
            size="medium"
            autoSpeak={true}
          />
        </div>

        <div className="text-center mb-8">
          <h1 
            className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Check Your Registration
          </h1>
          <p className="text-lg text-slate-600">
            Enter your vehicle registration number to view your status
          </p>
        </div>

        {/* Input */}
        <div className="w-full space-y-6">
          <div className="space-y-2">
            <Input
              value={vehicleReg}
              onChange={(e) => {
                setVehicleReg(e.target.value.toUpperCase());
                setError(null);
              }}
              onKeyPress={handleKeyPress}
              placeholder="e.g. AB12 CDE"
              className={`h-20 text-4xl text-center font-mono uppercase tracking-widest ${
                error ? 'border-red-500' : ''
              }`}
              data-testid="input-vehicle-lookup"
              autoFocus
            />
            {error && (
              <p className="text-red-500 text-center">{error}</p>
            )}
          </div>

          <button
            onClick={handleLookup}
            disabled={loading || !vehicleReg.trim()}
            className="kiosk-btn-primary w-full flex items-center justify-center gap-3"
            data-testid="lookup-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Checking...</span>
              </>
            ) : (
              <>
                <Search className="w-6 h-6" />
                <span>Check Registration</span>
              </>
            )}
          </button>
        </div>

        {/* Help text */}
        <p className="mt-8 text-slate-500 text-center">
          Your vehicle registration is printed on your number plate and V5C document
        </p>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t-2 border-slate-200">
        <button
          onClick={() => navigate("/kiosk")}
          className="kiosk-btn-secondary flex items-center gap-3"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
          <span>Back</span>
        </button>

        <button
          onClick={() => navigate("/kiosk/category")}
          className="text-blue-600 hover:text-blue-700 font-semibold text-lg"
          data-testid="register-new-link"
        >
          Register a new vehicle →
        </button>
      </div>
    </div>
  );
}
